import { useEffect, useState } from "react";
import api, { API } from "../lib/api";
import { getToken } from "../lib/tokenStorage";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { BookOpen, Youtube, Upload, Trash2, Plus, ExternalLink, FileText, Palette, Users, GraduationCap, ScrollText, Sparkles, Briefcase, Users2, Lock } from "lucide-react";

// `adminOnly` tabs (JDs, B2B, B2C) are hidden from the tab-bar for anyone
// who isn't Founder or BDOM. Backend already double-enforces this — the
// flag here is purely for UI cleanliness.
const CATEGORIES = [
  { key: "founder_video", label: "Founder's Vision & Welcome", icon: Sparkles, color: "text-amber-700 bg-amber-50" },
  { key: "training_video", label: "Staff Training", icon: GraduationCap, color: "text-blue-700 bg-blue-50" },
  { key: "handbook", label: "Employee Handbook", icon: BookOpen, color: "text-emerald-700 bg-emerald-50" },
  { key: "hr_policy", label: "HR Policies", icon: ScrollText, color: "text-red-700 bg-red-50" },
  { key: "marketing", label: "Marketing Essentials (Logo · Fonts · Colours)", icon: Palette, color: "text-fuchsia-700 bg-fuchsia-50" },
  { key: "job_description", label: "Job Descriptions", icon: Users, color: "text-slate-700 bg-slate-50" },
  { key: "b2b_docs", label: "B2B Docs", icon: Briefcase, color: "text-indigo-700 bg-indigo-50", adminOnly: true },
  { key: "b2c_docs", label: "B2C Docs", icon: Users2, color: "text-cyan-700 bg-cyan-50", adminOnly: true },
  { key: "other", label: "Other Documents", icon: FileText, color: "text-teal-700 bg-teal-50" },
];

function ytEmbedId(url) {
  const m = (url || "").match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}
function thumb(url) { const id = ytEmbedId(url); return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null; }

export default function StaffHub() {
  const { user } = useAuth();
  const isFounder = user.effective_role === "FOUNDER";
  // BDOM shares Founder-side privileges for admin-only tabs (JDs +
  // B2B/B2C decks). Anyone else never even sees the tab buttons.
  const isAdmin = user.effective_role === "FOUNDER" || user.effective_role === "BDOM";
  const visibleCats = CATEGORIES.filter(c => !c.adminOnly || isAdmin);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("founder_video");
  const [team, setTeam] = useState([]);
  const [form, setForm] = useState({ kind: "youtube", title: "", youtube_url: "", description: "", target_user_id: "", file: null, audience: "ALL", audience_destinations: "", audience_cities: "" });
  // Live reach preview — "X teammates will see this" based on the current
  // audience + destinations + cities picker. Re-fetches with a small
  // debounce so the Founder doesn't hammer the API on every keystroke.
  const [reach, setReach] = useState(null);
  useEffect(() => {
    if (!isFounder) return;
    const h = setTimeout(async () => {
      try {
        const { data } = await api.get("/staff-hub/audience-reach", {
          params: { audience: form.audience || "ALL", audience_destinations: form.audience_destinations || "", audience_cities: form.audience_cities || "" },
        });
        setReach(data.reach);
      } catch { setReach(null); }
    }, 300);
    return () => clearTimeout(h);
  }, [form.audience, form.audience_destinations, form.audience_cities, isFounder]);
  // Founder-only card-grid filter — narrow by role + destination + city.
  const [filter, setFilter] = useState({ audience: "", destination: "", city: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [{ data }, tRes] = await Promise.all([
      api.get("/staff-hub/items"),
      api.get("/users").catch(() => ({ data: [] })),
    ]);
    setItems(data || []);
    setTeam((tRes.data || []).filter(u => u.role !== "PARTICIPANT" && u.role !== "PARTNER"));
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      if (form.kind === "youtube") {
        if (!form.youtube_url.trim()) throw new Error("YouTube URL required");
        await api.post("/staff-hub/items/youtube", {
          category: active, title: form.title, youtube_url: form.youtube_url,
          description: form.description, target_user_id: form.target_user_id || null,
          audience: form.audience || "ALL",
          audience_destinations: (form.audience_destinations || "").split(",").map(s => s.trim()).filter(Boolean),
          audience_cities: (form.audience_cities || "").split(",").map(s => s.trim()).filter(Boolean),
        });
      } else {
        if (!form.file) throw new Error("Pick a file");
        const fd = new FormData();
        fd.append("file", form.file); fd.append("category", active); fd.append("title", form.title);
        fd.append("description", form.description); fd.append("target_user_id", form.target_user_id || "");
        fd.append("audience", form.audience || "ALL");
        fd.append("audience_destinations", form.audience_destinations || "");
        fd.append("audience_cities", form.audience_cities || "");
        await api.post("/staff-hub/items/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Uploaded");
      setForm({ kind: "youtube", title: "", youtube_url: "", description: "", target_user_id: "", file: null, audience: "ALL", audience_destinations: "", audience_cities: "" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || e?.message || "Failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this item?")) return;
    try { await api.delete(`/staff-hub/items/${id}`); toast.success("Removed"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleReview = async (it) => {
    // Optimistic — flip the row locally so the checkbox reacts instantly,
    // then reconcile with the server. If the call fails we reload to get
    // truth back.
    try {
      const { data } = await api.post(`/staff-hub/items/${it.id}/toggle-review`);
      setItems(prev => prev.map(r => r.id !== it.id ? r : {
        ...r,
        reviewed_by_id: data.reviewed ? data.reviewed_by_id : null,
        reviewed_by_name: data.reviewed ? data.reviewed_by_name : null,
        reviewed_at: data.reviewed ? data.reviewed_at : null,
      }));
      toast.success(data.reviewed ? "Marked reviewed" : "Sign-off cleared");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); await load(); }
  };

  // JD assignment modal — Founder/BDOM tap an "Orphan JD" chip and pick
  // the teammate inline; backend auto-archives any prior JDs for that
  // same teammate so history stays clean.
  const [assignTarget, setAssignTarget] = useState(null); // JD row being assigned
  const [assignUid, setAssignUid] = useState("");
  const submitAssign = async () => {
    if (!assignUid) return toast.error("Pick a teammate");
    try {
      await api.patch(`/staff-hub/items/${assignTarget.id}/target-user`, { target_user_id: assignUid });
      toast.success("JD assigned");
      setAssignTarget(null); setAssignUid("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Per-user Monday-digest opt-out — reads once on mount, then just
  // flips locally + persists on click. Hidden from the Founder (they
  // author the digest so opting out doesn't make sense).
  const [optOut, setOptOut] = useState(false);
  useEffect(() => {
    api.get("/staff-hub/digest/opt-out").then(r => setOptOut(!!r.data?.opt_out)).catch(() => {});
  }, []);
  const toggleOptOut = async () => {
    const next = !optOut;
    setOptOut(next);
    try { await api.post("/staff-hub/digest/opt-out", { opt_out: next }); toast.success(next ? "Digest silenced" : "Digest re-enabled"); }
    catch (e) { setOptOut(!next); toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // JD History drawer — Founder/BDOM click "N older versions" on a JD
  // card, we fetch the archived rows for the same teammate and show
  // them in a right-side drawer. Lazy — only fires on click.
  const [historyFor, setHistoryFor] = useState(null); // active JD row
  const [history, setHistory] = useState({ count: 0, items: [] });
  const [historyLoading, setHistoryLoading] = useState(false);
  const openHistory = async (it) => {
    setHistoryFor(it); setHistoryLoading(true);
    try {
      const { data } = await api.get(`/staff-hub/items/${it.id}/history`);
      setHistory(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); setHistory({ count: 0, items: [] }); }
    finally { setHistoryLoading(false); }
  };

  const shown = items.filter(i => {
    if (i.category !== active) return false;
    if (!isFounder) return true;  // audience filter is founder-only
    if (filter.audience && (i.audience || "ALL") !== filter.audience) return false;
    if (filter.destination) {
      const dests = (i.audience_destinations && i.audience_destinations.length
        ? i.audience_destinations
        : (i.audience_destination ? [i.audience_destination] : [])).map(d => d.toLowerCase());
      if (!dests.includes(filter.destination.toLowerCase())) return false;
    }
    if (filter.city) {
      const cities = (i.audience_cities && i.audience_cities.length
        ? i.audience_cities
        : (i.audience_city ? [i.audience_city] : [])).map(c => c.toLowerCase());
      if (!cities.includes(filter.city.toLowerCase())) return false;
    }
    return true;
  });
  const cat = CATEGORIES.find(c => c.key === active) || CATEGORIES[0];
  const CatIcon = cat.icon;
  // If a non-admin somehow lands on an admin-only tab (e.g. old bookmark),
  // silently bounce them back to the default tab.
  useEffect(() => {
    if (cat.adminOnly && !isAdmin) setActive("founder_video");
  }, [cat, isAdmin]);

  return (
    <div className="space-y-5" data-testid="staff-hub">
      <div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Aii Group · Internal</div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h1 className="text-2xl font-semibold flex items-center gap-2"><BookOpen className="text-brand"/> Staff Hub</h1>
          {!isFounder && (
            <button onClick={toggleOptOut} data-testid="sh-digest-optout"
                    className={`text-[11px] px-2.5 py-1 rounded-md border font-semibold transition ${optOut ? "bg-slate-100 text-slate-600 border-slate-300" : "bg-white text-brand border-[color:var(--aii-border)] hover:bg-[color:var(--aii-green-tint)]"}`}
                    title="Silence the Monday weekly digest email">
              {optOut ? "🔕 Monday digest silenced" : "🔔 Monday digest ON"}
            </button>
          )}
        </div>
        <p className="text-sm text-[color:var(--aii-text-2)] mt-1">Videos, handbooks, policies and marketing essentials for the whole team.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {visibleCats.map(c => (
          <button key={c.key} onClick={() => setActive(c.key)} data-testid={`staff-hub-cat-${c.key}`}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 ${active === c.key ? "bg-brand text-white" : "bg-white border border-[color:var(--aii-border)] hover:bg-[color:var(--aii-green-tint)]"}`}>
            <c.icon size={12}/> {c.label}
            {c.adminOnly && <Lock size={9} className="opacity-60" title="Founder + BDOM only"/>}
          </button>
        ))}
      </div>

      {isFounder && (
        <div className="aii-card p-5" data-testid="staff-hub-add">
          <div className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest px-2 py-1 rounded ${cat.color}`}>
            <CatIcon size={12}/> Add to {cat.label}
          </div>
          <div className="mt-3 grid sm:grid-cols-2 gap-2">
            <div className="flex gap-2">
              <button onClick={() => setForm(f => ({ ...f, kind: "youtube" }))} data-testid="kind-youtube" className={`flex-1 px-3 py-2 text-sm rounded-md border flex items-center justify-center gap-1.5 ${form.kind === "youtube" ? "bg-brand text-white border-brand" : "bg-white border-[color:var(--aii-border)]"}`}><Youtube size={13}/> YouTube link</button>
              <button onClick={() => setForm(f => ({ ...f, kind: "pdf" }))} data-testid="kind-pdf" className={`flex-1 px-3 py-2 text-sm rounded-md border flex items-center justify-center gap-1.5 ${form.kind === "pdf" ? "bg-brand text-white border-brand" : "bg-white border-[color:var(--aii-border)]"}`}><Upload size={13}/> PDF / PPT / DOCX</button>
            </div>
            <input placeholder="Title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="input-aii" data-testid="sh-title"/>
            {form.kind === "youtube" ? (
              <input placeholder="YouTube URL" value={form.youtube_url} onChange={e => setForm(f => ({ ...f, youtube_url: e.target.value }))} className="input-aii sm:col-span-2" data-testid="sh-yt"/>
            ) : (
              <input type="file" accept=".pdf,.ppt,.pptx,.doc,.docx" onChange={e => setForm(f => ({ ...f, file: e.target.files?.[0] || null }))} className="input-aii sm:col-span-2" data-testid="sh-file"/>
            )}
            <input placeholder="Short description (optional)" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="input-aii sm:col-span-2"/>
            {active === "job_description" && (
              <select value={form.target_user_id} onChange={e => setForm(f => ({ ...f, target_user_id: e.target.value }))} className="input-aii sm:col-span-2" data-testid="sh-target">
                <option value="">Which teammate is this JD for?</option>
                {team.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
            )}
            <select value={form.audience}
                    onChange={e => setForm(f => ({ ...f, audience: e.target.value }))}
                    data-testid="sh-audience"
                    className="input-aii">
              <option value="ALL">Visibility · All staff (default)</option>
              <option value="FOUNDER">Visibility · Founder only (private)</option>
              <option value="BDOM">Visibility · BDOM only</option>
              <option value="PCWM">Visibility · PCWM only</option>
              <option value="GEHM">Visibility · GEHM only</option>
              <option value="SMCM">Visibility · SMCM only</option>
            </select>
            {form.audience !== "ALL" && form.audience !== "FOUNDER" && (
              <>
                <input placeholder="Destinations (comma-separated · blank = any)" value={form.audience_destinations}
                       onChange={e => setForm(f => ({ ...f, audience_destinations: e.target.value }))}
                       data-testid="sh-audience-destinations"
                       className="input-aii"/>
                <input placeholder="Cities (comma-separated · blank = any)" value={form.audience_cities}
                       onChange={e => setForm(f => ({ ...f, audience_cities: e.target.value }))}
                       data-testid="sh-audience-cities"
                       className="input-aii"/>
              </>
            )}
          </div>
          {reach !== null && (
            <div className="mt-2 text-xs text-[color:var(--aii-muted)]" data-testid="sh-reach">
              👥 <b>{reach}</b> teammate{reach === 1 ? "" : "s"} will see this row with the current visibility settings.
            </div>
          )}
          <div className="mt-3 flex justify-end">
            <button onClick={submit} disabled={busy} data-testid="sh-submit" className="bg-brand text-white font-semibold px-4 py-2 rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1.5"><Plus size={14}/> {busy ? "Adding…" : "Add"}</button>
          </div>
        </div>
      )}

      {isFounder && (
        <div className="flex flex-wrap gap-2 items-center" data-testid="sh-filter">
          <span className="text-[10px] uppercase tracking-widest font-bold text-[color:var(--aii-muted)]">Audit view:</span>
          <select value={filter.audience} onChange={e => setFilter(f => ({ ...f, audience: e.target.value }))}
                  className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded-md bg-white">
            <option value="">Any role</option>
            <option value="ALL">ALL</option>
            <option value="FOUNDER">FOUNDER</option>
            <option value="BDOM">BDOM</option>
            <option value="PCWM">PCWM</option>
            <option value="GEHM">GEHM</option>
            <option value="SMCM">SMCM</option>
          </select>
          <input placeholder="Destination…" value={filter.destination}
                 onChange={e => setFilter(f => ({ ...f, destination: e.target.value }))}
                 className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded-md w-32"/>
          <input placeholder="City…" value={filter.city}
                 onChange={e => setFilter(f => ({ ...f, city: e.target.value }))}
                 className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded-md w-32"/>
          {(filter.audience || filter.destination || filter.city) && (
            <button onClick={() => setFilter({ audience: "", destination: "", city: "" })}
                    className="text-xs text-brand hover:underline">Clear</button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {shown.length === 0 && <div className="col-span-full aii-card p-8 text-center text-sm text-[color:var(--aii-muted)]">Nothing here yet.</div>}
        {shown.map(it => {
          const targetName = it.target_user_id ? team.find(t => t.id === it.target_user_id)?.name : null;
          return (
            <div key={it.id} data-testid={`sh-card-${it.id}`} className="aii-card overflow-hidden flex flex-col">
              {it.kind === "youtube" ? (
                <a href={it.youtube_url} target="_blank" rel="noopener noreferrer" className="aspect-video bg-slate-900 relative group">
                  {thumb(it.youtube_url) ? <img src={thumb(it.youtube_url)} alt="" className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center text-white/50"><Youtube size={40}/></div>}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition"><Youtube size={48} className="text-white drop-shadow-lg"/></div>
                </a>
              ) : (
              <div className="aspect-video bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                <a href={`${API}/files/${it.file_id}?auth=${getToken()}`} target="_blank" rel="noopener noreferrer">
                  <FileText size={48} className="text-brand"/>
                </a>
              </div>
              )}
              <div className="p-4 flex-1 flex flex-col">
                <div className="font-display font-semibold text-base leading-tight">{it.title}</div>
                {targetName && <div className="text-[11px] text-brand font-semibold mt-0.5">JD · {targetName}</div>}
                {isAdmin && active === "job_description" && !it.target_user_id && (
                  <button onClick={() => { setAssignTarget(it); setAssignUid(""); }}
                          data-testid={`sh-orphan-${it.id}`}
                          className="text-[10px] uppercase tracking-widest font-bold mt-1 px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200 self-start hover:bg-red-100 transition">
                    ⚠ Orphan JD · tap to assign
                  </button>
                )}
                {isAdmin && (active === "b2b_docs" || active === "b2c_docs") && (
                  <button onClick={() => toggleReview(it)}
                          data-testid={`sh-review-${it.id}`}
                          className={`text-[10px] uppercase tracking-widest font-bold mt-1 px-1.5 py-0.5 rounded self-start border transition ${it.reviewed_by_id ? "bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
                    {it.reviewed_by_id
                      ? `✓ Reviewed by ${it.reviewed_by_name}${it.reviewed_at ? " · " + it.reviewed_at.slice(0,10) : ""}`
                      : "☐ Mark reviewed"}
                  </button>
                )}
                {isFounder && it.audience && it.audience !== "ALL" && (
                  <div className="text-[10px] uppercase tracking-widest font-bold mt-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 self-start" data-testid={`sh-audience-badge-${it.id}`}>
                    {it.audience}
                    {(() => {
                      const dests = (it.audience_destinations && it.audience_destinations.length)
                        ? it.audience_destinations
                        : (it.audience_destination ? [it.audience_destination] : []);
                      const cities = (it.audience_cities && it.audience_cities.length)
                        ? it.audience_cities
                        : (it.audience_city ? [it.audience_city] : []);
                      return (dests.length ? ` · ${dests.join(", ")}` : "") + (cities.length ? ` · ${cities.join(", ")}` : "");
                    })()}
                  </div>
                )}
                {isAdmin && active === "job_description" && it.target_user_id && (
                  <button onClick={() => openHistory(it)}
                          data-testid={`sh-history-${it.id}`}
                          className="text-[10px] uppercase tracking-widest font-bold mt-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-300 self-start hover:bg-slate-200 transition">
                    🕘 Show older versions
                  </button>
                )}
                {it.description && <p className="text-xs text-[color:var(--aii-text-2)] mt-1 line-clamp-2">{it.description}</p>}
                <div className="mt-3 flex items-center justify-between gap-2">
                  <a href={it.kind === "youtube" ? it.youtube_url : `${API}/files/${it.file_id}?auth=${getToken()}`} target="_blank" rel="noopener noreferrer" className="text-xs text-brand hover:underline flex items-center gap-1"><ExternalLink size={11}/> Open</a>
                  {isFounder && <button onClick={() => del(it.id)} data-testid={`sh-del-${it.id}`} className="text-[color:var(--aii-muted)] hover:text-red-600"><Trash2 size={13}/></button>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {assignTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="jd-assign-modal" onClick={() => setAssignTarget(null)}>
          <div className="bg-white rounded-lg max-w-md w-full p-5 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Assign JD</div>
            <h3 className="font-display font-semibold text-lg mt-0.5">{assignTarget.title}</h3>
            <p className="text-xs text-[color:var(--aii-muted)] mt-1">Pick the teammate this JD belongs to. Any earlier JD they had will be auto-archived so only this version stays live.</p>
            <select value={assignUid} onChange={e => setAssignUid(e.target.value)} className="input-aii mt-3" data-testid="jd-assign-select">
              <option value="">— Pick a teammate —</option>
              {team.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role}{u.destination ? ` · ${u.destination}` : ""})</option>)}
            </select>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setAssignTarget(null)} className="text-sm px-3 py-1.5 text-slate-600 hover:text-slate-900">Cancel</button>
              <button onClick={submitAssign} disabled={!assignUid} data-testid="jd-assign-save"
                      className="text-sm px-4 py-1.5 bg-brand text-white font-semibold rounded-md hover:bg-brand-dark disabled:opacity-50">
                Assign & archive prior
              </button>
            </div>
          </div>
        </div>
      )}

      {historyFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="jd-history-drawer" onClick={() => setHistoryFor(null)}>
          <div className="bg-white rounded-lg max-w-lg w-full p-5 shadow-xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-brand font-bold">JD version history</div>
                <h3 className="font-display font-semibold text-lg mt-0.5">{team.find(t => t.id === historyFor.target_user_id)?.name || "Teammate"}</h3>
                <p className="text-xs text-[color:var(--aii-muted)] mt-0.5">Current live version: <b>{historyFor.title}</b> · uploaded {historyFor.created_at?.slice(0,10)}</p>
              </div>
              <button onClick={() => setHistoryFor(null)} className="text-slate-500 hover:text-slate-900 text-xl leading-none">×</button>
            </div>
            <div className="mt-4 border-t border-slate-200 pt-3">
              {historyLoading ? (
                <div className="text-sm text-[color:var(--aii-muted)]">Loading…</div>
              ) : history.count === 0 ? (
                <div className="text-sm text-[color:var(--aii-muted)] italic">No prior versions — this is the first JD uploaded for this teammate.</div>
              ) : (
                <>
                  <div className="text-[11px] text-[color:var(--aii-muted)] mb-2">{history.count} archived version{history.count === 1 ? "" : "s"} — newest first.</div>
                  <ul className="space-y-2">
                    {history.items.map(h => (
                      <li key={h.id} data-testid={`jdh-row-${h.id}`} className="border border-slate-200 rounded p-2 bg-slate-50">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">{h.title}</div>
                            <div className="text-[11px] text-[color:var(--aii-muted)]">
                              Uploaded {h.created_at?.slice(0,10)}{h.created_by_name ? ` · by ${h.created_by_name}` : ""}
                              {h.archived_at ? <> · archived {h.archived_at.slice(0,10)}</> : null}
                            </div>
                          </div>
                          <a href={h.kind === "youtube" ? h.youtube_url : `${API}/files/${h.file_id}?auth=${getToken()}`}
                             target="_blank" rel="noopener noreferrer"
                             className="text-[11px] text-brand hover:underline font-semibold shrink-0 flex items-center gap-1">
                            <ExternalLink size={10}/> Open
                          </a>
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
